
import React from 'react';
import { Meeting } from '@/types/meeting';
import { TabNavigation, Tab } from './navigation/TabNavigation';
import { MeetingFormContent } from './MeetingFormContent';
import { RecurrenceForm } from './RecurrenceForm';
import { ModalLayout } from './layout/modalLayout';
import {
  LayoutDashboard,
  Users,
  FileText,
  ClipboardList,
  MessageSquare,
  CheckSquare,
  Files
} from 'lucide-react';

const TABS: Tab[] = [
  { id: 'details', label: 'Meeting Details', icon: <LayoutDashboard className="w-4 h-4" /> },
  { id: 'templates', label: 'Templates', icon: <FileText className="w-4 h-4" /> },
  { id: 'attendees', label: 'Attendees', icon: <Users className="w-4 h-4" /> },
  { id: 'agenda', label: 'Agenda', icon: <ClipboardList className="w-4 h-4" /> },
  { id: 'minutes', label: 'Minutes', icon: <MessageSquare className="w-4 h-4" /> },
  { id: 'tasks', label: 'Tasks', icon: <CheckSquare className="w-4 h-4" /> },
  { id: 'files', label: 'Files', icon: <Files className="w-4 h-4" /> },
];

interface MeetingFormProps {
  onSubmit: (meeting: Partial<Meeting>) => void;
  onCancel: () => void;
  initialData?: Partial<Meeting>;
}

export function MeetingForm({ onSubmit, onCancel, initialData }: MeetingFormProps) {
  const [formData, setFormData] = React.useState<Partial<Meeting>>(
    initialData || {
      title: '',
      type: 'PARENT_TEACHER',
      description: '',
      startTime: new Date(Date.now() + 3600000),
      endTime: new Date(Date.now() + 7200000),
      location: '',
      isVirtual: false,
      status: 'SCHEDULED',
      participants: [],
      attachments: [],
      tags: [],
    }
  );

  const [activeTab, setActiveTab] = React.useState('details');
  const [showRecurrence, setShowRecurrence] = React.useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="h-full">
      <ModalLayout
        title={initialData ? 'Edit Meeting' : 'Schedule New Meeting'}
        footer={
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700"
            >
              {initialData ? 'Update Meeting' : 'Create Meeting'}
            </button>
          </div>
        }
      >
        <div className="flex flex-col flex-1 min-h-0">
          <div className="sticky top-0 z-10 bg-white shadow-sm">
            <TabNavigation
              tabs={TABS}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
          </div>

          <div className="flex-1 overflow-y-auto">
            <MeetingFormContent
              activeTab={activeTab}
              formData={formData}
              setFormData={setFormData}
              onShowRecurrence={() => setShowRecurrence(true)}
              setActiveTab={setActiveTab}
            />
          </div>
        </div>
      </ModalLayout>

      {showRecurrence && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <RecurrenceForm
            value={formData.recurrence}
            onChange={(pattern) => {
              setFormData(prev => ({ ...prev, recurrence: pattern }));
              if (!pattern) setShowRecurrence(false);
            }}
            onClose={() => setShowRecurrence(false)}
          />
        </div>
      )}
    </form>
  );
}